import { Injectable, inject } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { anonymousBrandStorageKey, buildLogoutRedirectUrl } from '@ninety/platform/models/login/anonymous-brand';
import { CookieService } from '@ninety/platform/services/services/cookie.service';
import { LocalStorageService, SessionStorageService } from '@ninety/platform/services/services/storage.service';
import { TokenService } from '@ninety/platform/services/services/token.service';
import { WINDOW } from '@ninety/platform/services/services/window.token';

import { BaseEffects } from '../../../base.effects';
import { EntitlementsStateActions } from '../../entitlements/entitlements-state.actions';
import { SpinnerActions } from '../../spinner/spinner-state.actions';
import { AmplifyService } from '../services/amplify.service';

import { LogOutActions } from './logout.actions';

const apiV4 = 'api/v4';

@Injectable()
export class LogOutEffects extends BaseEffects {
  private readonly amplifyService = inject(AmplifyService);
  private readonly tokenService = inject(TokenService);
  private readonly cookieService = inject(CookieService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly window = inject(WINDOW);

  logoutApis$ = this.switchMapEffect({
    action: [LogOutActions.initiateLogOut, LogOutActions.initiateLogOutWithSuppliedRedirect],
    switchMapFn: action => {
      const logoutRedirectUrl = 'logoutRedirectUrl' in action ? action.logoutRedirectUrl : undefined;
      return forkJoin([this.http.get(`${apiV4}/Logout`), this.http.get(`${apiV4}/LogoutDomain`)]).pipe(
        map(() => LogOutActions.apiLoggedOut({ logoutRedirectUrl })),
        catchError(() => of(LogOutActions.apiLoggedOut({ logoutRedirectUrl })))
      );
    },
  });

  clearEntitlementsTokenOnLogout$ = this.mapEffect({
    action: [LogOutActions.initiateLogOut, LogOutActions.initiateLogOutWithSuppliedRedirect],
    mapFn: () => EntitlementsStateActions.clearEntitlements(),
  });

  removeTokensAndCookies$ = this.mapEffect({
    action: LogOutActions.apiLoggedOut,
    mapFn: params => {
      this.sessionStorageService.delete('lastAccessedTeamId');
      this.tokenService.removeAccessToken();
      this.tokenService.removeEntitlementsToken();
      this.cookieService.deleteNinetyAll();
      this.cookieService.deleteAll();

      /*
        Manually remove cognito items from local storage.  We have an issue
        with switching betweem multiple different logins that can cause some
        endless looping issues.
      */
      Object.keys(localStorage)
        .filter(key => key.startsWith('CognitoIdentityServiceProvider'))
        .forEach(key => localStorage.removeItem(key));

      return LogOutActions.navigateToLogin({ logoutRedirectUrl: params.logoutRedirectUrl });
    },
  });

  /*
    Amplify SignOut causes a redirect to the login URL.  Amplify Signout must
    happen after the session storage tokens are removed.  Otherwise, the Ninety
    token will cause the app to redirect to the Home page.
  */
  amplifySignOut$ = this.switchMapEffect({
    action: LogOutActions.navigateToLogin,
    switchMapFn: () =>
      this.amplifyService.signOut().pipe(
        map(() => LogOutActions.amplifyLoggedOut()),
        // We don't care if there is an error, return the same value
        catchError(() => of(LogOutActions.amplifyLoggedOut()))
      ),
  });

  /*
   Using a full refresh here by directly updating the window.location.href
   instead of using the Angular Router. The refresh here will guarantee
   the clearing of state from the store and services.

   This is also a backup redirect to login in case Amplify.SignOut doesn't do
   the redirect.

   Allows an optional logoutRedirectUrl to be supplied, if it's not it will redirect to /Login,
   carrying forward the brand the visitor arrived on (`?bos=`) so the login screen they land on
   matches the one they signed in from. Without it the login page has nothing left to read: the
   login flow deletes LOGIN_PARTNER_CONFIGURATION on every successful login, so the brand ladder
   would fall through to its eos default.
  */
  navigateToLogin$ = this.tapEffect({
    action: LogOutActions.navigateToLogin,
    tapFn: params => {
      if (params.logoutRedirectUrl) {
        this.window.location.href = params.logoutRedirectUrl;
      } else {
        this.window.location.href = buildLogoutRedirectUrl(this.localStorageService.get(anonymousBrandStorageKey));
      }
    },
  });

  startSpinner$ = this.mapEffect({
    action: LogOutActions.initiateLogOut,
    mapFn: ({ type }) => SpinnerActions.startPrimary({ source: type }),
  });
}
